/**
 * The four seconds that matter.
 *
 * Two things live here: the golfer's swing as a clock, and the ball's crouch as
 * an analog verb. They meet exactly once, at impact, and the entire game is the
 * distance between the ball's centre and the clubface sweet spot at that instant.
 *
 * There is deliberately no "flinch" rule and no special-case punishment for
 * hesitating. A mistimed hop is simply a bad offset, and the geometry says so.
 */

import { speedForDistance } from './ballistics'
import type { Rng } from './rng'
import * as T from './tuning'
import type {
	Contact,
	ContactTier,
	GolferSpec,
	LaunchParams,
	LieSpec,
	SwingStage,
	TeeMotion,
	Vec3,
} from './types'

// ── Club kinematics ──────────────────────────────────────────────────────────

/**
 * Forward kinematics for the club, matching the scene graph term for term:
 * the club hangs along local −Y and is rotated about +X. The renderer applies
 * the same transform, so the drawn clubhead and the simulated one cannot drift
 * apart — and by construction the arc passes exactly through the sweet spot at
 * SWING_ANGLE_IMPACT.
 */
export function clubheadAt(angle: number): Vec3 {
	return {
		x: T.PIVOT.x,
		y: T.PIVOT.y - T.CLUB_LEN * Math.cos(angle),
		z: T.PIVOT.z - T.CLUB_LEN * Math.sin(angle),
	}
}

const smoothstep = (x: number) => {
	const t = Math.min(1, Math.max(0, x))
	return t * t * (3 - 2 * t)
}
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const clamp01 = (x: number) => Math.min(1, Math.max(0, x))

// ── Swing events ─────────────────────────────────────────────────────────────

export type SwingEventKind =
	| 'squeak'
	| 'backswing'
	| 'top'
	| 'downswing'
	| 'tick'
	| 'tickFinal'
	| 'phone'
	| 'impact'
	| 'settled'
	| 'stormOff'

export type SwingEvent = { kind: SwingEventKind }

// ── The swing clock ──────────────────────────────────────────────────────────

export class Swing {
	golfer: GolferSpec
	stage: SwingStage = 'ADDRESS'
	/** Seconds elapsed inside the current stage. */
	t = 0
	anger = 0
	swingsTaken = 0
	/** True once this golfer has walked off; the encounter is over. */
	stormedOff = false
	/**
	 * Their phone went off at the top of this swing. The strike is ruined and
	 * ducking it is free — the caller checks this before charging anger.
	 */
	phoneRang = false
	/** Set the frame the club reaches the ball, consumed by the caller. */
	impactPending = false
	/**
	 * How far through the step that fired 'impact' the club actually reached the
	 * ball, 0..1. The strike is decided by where the ball was at that instant, so
	 * the caller interpolates the ball's height rather than reading it a whole
	 * step late — off the apex the ball moves fast enough that one step is worth
	 * over a centimetre, which is half the flush window.
	 */
	impactFrac = 1

	private stageDur = 0
	/** Per-swing tempo wobble, rolled fresh at each address. */
	private wobble = 1
	private squeaksLeft = 0
	private nextSqueakAt = 0
	private ticksFired = 0
	private rng: Rng

	constructor(golfer: GolferSpec, rng: Rng) {
		this.golfer = golfer
		this.rng = rng
		this.beginAddress(true)
	}

	get tempo(): number {
		return this.golfer.tempoMult
	}
	get downswingDur(): number {
		return (T.ANGER[this.anger] ?? T.ANGER[0]!).downswing * this.tempo * this.wobble
	}
	get backswingDur(): number {
		return T.BACKSWING_TIME * this.tempo
	}
	get topDur(): number {
		return T.TOP_PAUSE * this.tempo
	}
	get angerName(): string {
		return (T.ANGER[this.anger] ?? T.ANGER[0]!).name
	}

	/** Simulation seconds until the clubface reaches the ball. Infinity when no
	 *  strike is inbound, which is what silences the telegraph between swings. */
	get tToImpact(): number {
		switch (this.stage) {
			case 'BACKSWING':
				return this.stageDur - this.t + this.topDur + this.downswingDur
			case 'TOP':
				return this.stageDur - this.t + this.downswingDur
			case 'DOWNSWING':
				return Math.max(0, this.stageDur - this.t)
			default:
				return Number.POSITIVE_INFINITY
		}
	}

	/** Where the clubface sweet spot is right now. */
	get swingAngle(): number {
		const p = this.stageDur > 0 ? clamp01(this.t / this.stageDur) : 1
		switch (this.stage) {
			case 'ADDRESS':
				return T.SWING_ANGLE_ADDRESS
			case 'BACKSWING':
				return lerp(T.SWING_ANGLE_ADDRESS, T.SWING_ANGLE_TOP, smoothstep(p))
			case 'TOP':
				return T.SWING_ANGLE_TOP
			case 'DOWNSWING':
				// Accelerating through the ball: ease in, never ease out.
				return lerp(T.SWING_ANGLE_TOP, T.SWING_ANGLE_IMPACT, p * p * (2 - p * p))
			case 'FOLLOW':
				return lerp(T.SWING_ANGLE_IMPACT, T.SWING_ANGLE_FOLLOW, smoothstep(p))
			default:
				return T.SWING_ANGLE_ADDRESS
		}
	}

	/** How far through the current downswing, for the timescale ramp. */
	get rampTimescale(): number {
		const tti = this.tToImpact
		// Floored so that tuning the shortening up to 1/MAX_ANGER can never divide
		// the ramp by zero and hand the caller a NaN timescale.
		const rampTime = Math.max(0.05, T.RAMP_TIME * (1 - T.RAMP_ANGER_SHORTEN * this.anger))
		if (!Number.isFinite(tti) || tti > rampTime) return 1
		const floor = lerp(T.RAMP_TIMESCALE, T.RAMP_FLOOR_AT_MAX_ANGER, this.anger / T.MAX_ANGER)
		return lerp(1, floor, smoothstep(1 - tti / rampTime))
	}

	beginAddress(first: boolean): void {
		this.stage = 'ADDRESS'
		this.t = 0
		this.stageDur = first
			? this.rng.range(T.ADDRESS_MIN, T.ADDRESS_MAX) * this.tempo
			: T.ADDRESS_REPEAT * this.tempo
		// The glove re-grip is the anger tell you can hear without looking.
		this.squeaksLeft = this.anger + 1
		this.nextSqueakAt = this.stageDur * 0.35
		this.ticksFired = 0
		this.impactPending = false
		this.phoneRang = false
		// A steady golfer swings the same way every time; a drunk one does not.
		// The ticks still fire at the honest moment, so this is unreliable rather
		// than unfair — you just cannot do it from memory.
		this.wobble = 1 + this.rng.range(-1, 1) * this.golfer.chaos
	}

	/** Advance the swing by one simulation step. */
	step(dt: number, out: SwingEvent[]): void {
		if (this.stormedOff) return
		this.t += dt

		if (this.stage === 'ADDRESS' && this.squeaksLeft > 0 && this.t >= this.nextSqueakAt) {
			out.push({ kind: 'squeak' })
			this.squeaksLeft--
			this.nextSqueakAt = this.t + 0.16
		}

		// The three-beat telegraph, measured in simulation time so a compressed
		// angry swing stays a learnable rhythm rather than a reflex test.
		const tti = this.tToImpact
		while (this.ticksFired < T.TICKS.length) {
			const at = T.TICKS[this.ticksFired]!
			if (tti > at) break
			out.push({ kind: this.ticksFired === T.TICKS.length - 1 ? 'tickFinal' : 'tick' })
			this.ticksFired++
		}

		if (this.t < this.stageDur) return

		const overshoot = this.t - this.stageDur
		switch (this.stage) {
			case 'ADDRESS':
				this.stage = 'BACKSWING'
				this.stageDur = this.backswingDur
				this.t = overshoot
				out.push({ kind: 'backswing' })
				break
			case 'BACKSWING':
				this.stage = 'TOP'
				this.stageDur = this.topDur
				this.t = overshoot
				// The silence at the top is the tell. Audio ducks here.
				out.push({ kind: 'top' })
				// And occasionally the silence is broken by a phone.
				if (this.rng.chance(T.PHONE_CHANCE)) {
					this.phoneRang = true
					out.push({ kind: 'phone' })
				}
				break
			case 'TOP':
				this.stage = 'DOWNSWING'
				this.stageDur = this.downswingDur
				this.t = overshoot
				out.push({ kind: 'downswing' })
				break
			case 'DOWNSWING':
				this.stage = 'FOLLOW'
				this.stageDur = T.FOLLOW_TIME * this.tempo
				this.t = overshoot
				this.swingsTaken++
				this.impactPending = true
				this.impactFrac = dt > 0 ? clamp01((dt - overshoot) / dt) : 1
				out.push({ kind: 'impact' })
				break
			case 'FOLLOW':
				this.stage = 'RESET'
				this.stageDur = 0
				this.t = 0
				out.push({ kind: 'settled' })
				break
			default:
				break
		}
	}

	/**
	 * The club missed. Anger climbs, and the golfer may walk off — a roll, not a
	 * fuse. With a deterministic fuse the expected score rose monotonically with
	 * every dodge, so max-dodging was simply correct and the gamble was fake.
	 */
	registerWhiff(out: SwingEvent[]): { stormedOff: boolean; angerBefore: number } {
		const angerBefore = this.anger
		const p = Math.min(1, (T.ANGER[this.anger] ?? T.ANGER[0]!).stormOff * this.golfer.temperMult)
		if (this.rng.chance(p)) {
			this.stormedOff = true
			out.push({ kind: 'stormOff' })
			return { stormedOff: true, angerBefore }
		}
		this.anger = Math.min(T.MAX_ANGER, this.anger + 1)
		return { stormedOff: false, angerBefore }
	}
}

// ── The ball on the tee, or wherever it ended up ─────────────────────────────

export type TeeBall = {
	motion: TeeMotion
	/** Local Y of the ball centre, measured from the ground it is sitting on. */
	y: number
	vy: number
	/** 0..1 crouch charge; also the crouch depth. */
	charge: number
	/** 0..1 for the renderer: 1 is a pancake, 0 is a sphere. */
	squash: number
	hops: number
}

export function createTeeBall(lie: LieSpec = T.LIES.tee!): TeeBall {
	return { motion: 'REST', y: lie.restY, vy: 0, charge: 0, squash: 0, hops: 0 }
}

export function resetTeeBall(b: TeeBall, lie: LieSpec = T.LIES.tee!): void {
	b.motion = 'REST'
	b.y = lie.restY
	b.vy = 0
	b.charge = 0
	b.squash = 0
	b.hops = 0
}

/** Where the clubface sweet spot passes for this lie. */
export const sweetYFor = (lie: LieSpec) => lie.restY + lie.sweetRise

/** Apex the ball reaches for a given charge, in world Y. */
export function apexForCharge(charge: number, lie: LieSpec = T.LIES.tee!): number {
	const v = (T.HOP_V_MIN + (T.HOP_V_MAX - T.HOP_V_MIN) * clamp01(charge)) * lie.hopMult
	return lie.restY + (v * v) / (2 * T.GRAVITY)
}

/** The highest the ball can get from this lie, however well it is played. */
export const maxApex = (lie: LieSpec) => apexForCharge(1, lie)

/**
 * The charge that puts the apex exactly on the sweet spot — or 1 when the sweet
 * spot is simply out of reach, which is what a bad lie means. Deep grass does
 * not make the timing harder, it lowers the ceiling: from the rough a perfect
 * release still only earns a GOOD, and from sand a THIN.
 */
export function flushCharge(lie: LieSpec = T.LIES.tee!): number {
	const need = Math.sqrt(2 * T.GRAVITY * lie.sweetRise) / lie.hopMult
	return clamp01((need - T.HOP_V_MIN) / (T.HOP_V_MAX - T.HOP_V_MIN))
}

/** The best contact this lie physically allows, for the readout. */
export function lieCeiling(lie: LieSpec): ContactTier {
	const best = Math.abs(maxApex(lie) - sweetYFor(lie))
	// The ball can always come *down* through the sweet spot too, so anything it
	// can reach at all is reachable exactly.
	const off = maxApex(lie) >= sweetYFor(lie) ? 0 : best
	if (off <= T.TIER_FLUSH) return 'FLUSH'
	if (off <= T.TIER_GOOD) return 'GOOD'
	if (off <= T.TIER_THIN) return 'THIN'
	if (off <= T.TIER_SHANK) return 'SHANK'
	return 'WHIFF'
}

export type TeeEvent = 'squat' | 'hop' | 'land'

/**
 * One simulation step of the crouch.
 *
 * Releasing launches the ball so that its apex lands where the charge says it
 * should, measured from the resting height rather than from the crouch. That
 * keeps the motion continuous (no pop off the ground) while letting the charge
 * map straight onto apex height, which is the property that makes this verb
 * latency-tolerant: at the apex the ball is barely moving, so the forgiveness
 * window is generous exactly where the player is aiming.
 */
export function stepTeeBall(
	b: TeeBall,
	dt: number,
	holding: boolean,
	out: TeeEvent[],
	lie: LieSpec = T.LIES.tee!,
): void {
	// You cannot crouch far into ground you are already sitting on.
	const squat = Math.min(T.SQUAT_DEPTH, Math.max(0, lie.restY - 0.004))

	if (b.motion === 'AIRBORNE') {
		b.vy -= T.GRAVITY * dt
		b.y += b.vy * dt
		if (b.y <= lie.restY && b.vy < 0) {
			b.y = lie.restY
			b.vy = 0
			b.motion = 'REST'
			b.charge = 0
			out.push('land')
		}
		b.squash = 0
		return
	}

	if (holding) {
		if (b.motion !== 'SQUATTING') {
			b.motion = 'SQUATTING'
			out.push('squat')
		}
		b.charge = Math.min(1, b.charge + dt / T.SQUAT_TIME)
	} else if (b.motion === 'SQUATTING') {
		const apex = apexForCharge(b.charge, lie)
		b.vy = Math.sqrt(Math.max(0, 2 * T.GRAVITY * (apex - b.y)))
		b.motion = 'AIRBORNE'
		b.hops++
		out.push('hop')
		return
	} else if (b.charge > 0) {
		b.charge = Math.max(0, b.charge - dt / T.UNSQUAT_TIME)
	}

	b.y = lie.restY - squat * b.charge
	b.squash = b.charge
}

// ── The strike ───────────────────────────────────────────────────────────────

const tierFor = (absOffset: number): ContactTier => {
	if (absOffset <= T.TIER_FLUSH) return 'FLUSH'
	if (absOffset <= T.TIER_GOOD) return 'GOOD'
	if (absOffset <= T.TIER_THIN) return 'THIN'
	if (absOffset <= T.TIER_SHANK) return 'SHANK'
	return 'WHIFF'
}

/**
 * Resolve the strike. `ballY` is the ball centre at the impact frame.
 *
 * Negative offset means the club arrived above the ball's centre and caught its
 * crown: a topped screamer. Positive means it slid underneath: a sky ball.
 */
export function resolveContact(
	ballY: number,
	golfer: GolferSpec,
	lie: LieSpec = T.LIES.tee!,
): Contact {
	const offset = ballY - sweetYFor(lie)
	const raw = Math.abs(offset)

	// Whether the club reached the ball at all is *geometry*, and geometry does
	// not care whose club it is. Scaling this boundary too would mean a golfer
	// with a generous sweet spot is literally impossible to duck: at 1.6x the
	// whiff line moves to 22.4cm, past the 17.5cm a full crouch can reach, so
	// the club would sail visibly over the ball and still be scored a shank.
	const missed = raw > T.TIER_SHANK

	// Within the face, a generous sweet spot widens the good tiers. That is the
	// whole of Dana, and it is the only thing the multiplier should touch.
	const scaled = Math.min(raw / golfer.sweetSpotMult, T.TIER_SHANK)
	const tier = missed ? 'WHIFF' : tierFor(scaled)
	const quality = missed ? 0 : clamp01(1 - (scaled / T.TIER_SHANK) ** 1.6)

	let offlineDeg: number = T.OFFLINE_DEG.FLUSH
	let spinBack: number = T.SPIN_BACK.FLUSH
	if (tier === 'GOOD') {
		offlineDeg = T.OFFLINE_DEG.GOOD
		spinBack = T.SPIN_BACK.GOOD
	} else if (tier === 'THIN') {
		offlineDeg = T.OFFLINE_DEG.THIN
		spinBack = offset < 0 ? T.SPIN_BACK.THIN_LOW : T.SPIN_BACK.THIN_HIGH
	} else if (tier === 'SHANK') {
		offlineDeg = T.OFFLINE_DEG.SHANK
		spinBack = T.SPIN_BACK.SHANK
	}

	return {
		tier,
		offset,
		quality,
		smash: T.SMASH[tier],
		offlineDeg,
		spinBack,
		damage: T.TIER_DAMAGE[tier],
	}
}

/** Launch angle for a strike, keyed off which side of the sweet spot it caught. */
function launchDegFor(c: Contact): number {
	switch (c.tier) {
		case 'FLUSH':
			return T.LAUNCH_DEG.FLUSH
		case 'GOOD':
			return T.LAUNCH_DEG.GOOD
		case 'THIN':
			return c.offset < 0 ? T.LAUNCH_DEG.THIN_LOW : T.LAUNCH_DEG.THIN_HIGH
		default:
			return T.LAUNCH_DEG.SHANK
	}
}

export type LaunchInputs = {
	contact: Contact
	golfer: GolferSpec
	anger: number
	goldenHour: boolean
	/** Their phone went off at the top. Whatever happens next is not your fault. */
	phone?: boolean
	/** Where the ball was sitting. Turf steals power a tee does not. */
	lie?: LieSpec
	/**
	 * How far away the hole is, and which way. Golfers are not idiots: from 47m
	 * they hit a wedge, not a driver, and they aim at the flag rather than down
	 * the middle. Without this the hole can never be converged on and playing it
	 * out is impossible — every shot is a full drive past the green.
	 */
	target?: { dist: number; bearingDeg: number }
	rng: Rng
}

/**
 * How hard they swing for a given distance, as a fraction of a full shot.
 *
 * Carry grows roughly as speed^1.3 in this flight model, so the inverse is the
 * 0.77 power. Floored so a golfer always makes contact with something, and
 * capped at 1 because nobody hits it further than flat out.
 */
/**
 * Golfers aim to *land* it short and let it run to the hole, because a ball
 * that carries the whole distance then rolls the rest of the way past.
 *
 * Without this, striking it perfectly was worse than striking it well: a flush
 * strike carried exactly to the flag and released over the back, while a
 * slightly weak one pitched short and trickled up. Aiming for the front takes
 * that inversion out and is what a real golfer does anyway.
 */
export const APPROACH_LANDING = 0.88

export function clubSpeed(dist: number, lie: LieSpec): number {
	// A putt never leaves the ground, so its distance is pure roll-out on a green
	// that sheds only 0.6 m/s^2 — seven times slower than fairway. Run it through
	// the lofted-shot table and an eighteen metre putt goes eighty metres past.
	if (lie.maxLaunchDeg !== undefined) {
		const decel = T.SURFACES.green?.rollDecel ?? 0.6
		return Math.sqrt(2 * decel * Math.max(0.4, dist))
	}
	return speedForDistance(dist * APPROACH_LANDING)
}

export function clubPower(distanceToPin: number, fullSpeed: number): number {
	if (fullSpeed <= 0) return 1
	// The needed speed comes from the flight model itself, because no single
	// exponent fits both ends: a drive is drag-limited and its distance grows as
	// speed^1.3, while a pitch is nearly drag-free and grows as speed^2. Fitting
	// the long end left a 21m shot travelling 4m and the hole unfinishable.
	// Floored only so a golfer always makes some contact with something.
	return Math.min(1, Math.max(0.03, speedForDistance(distanceToPin) / fullSpeed))
}

/** Turn a strike into initial flight conditions. */
export function buildLaunch(i: LaunchInputs): LaunchParams {
	const tier = T.ANGER[i.anger] ?? T.ANGER[0]!
	const golden = i.goldenHour ? 1 + T.GOLDEN_POWER_BONUS : 1
	const phone = i.phone ? T.PHONE_SMASH : 1
	const lie = i.lie ?? T.LIES.tee!
	// The club they chose for the distance left. Anger still multiplies on top,
	// which is exactly why a furious golfer sails a wedge over the back of the
	// green. Without this the hole cannot be converged on at all: every shot from
	// 46m out is a full 170m drive and nobody ever gets down.
	// Measured against what *this* golfer's flat-out swing produces, so a weak
	// golfer swings a bigger fraction of their own maximum for the same yardage.
	// The club is chosen so that after the lie's own power loss the ball leaves at
	// the speed the distance actually needs. Contact quality and anger then
	// multiply on top, which is where the game is.
	const club = i.target
		? Math.min(
				1,
				Math.max(
					0.02,
					clubSpeed(i.target.dist, lie) / (T.BASE_SPEED * i.golfer.speedMult * lie.speedMult),
				),
			)
		: 1
	const speed =
		T.BASE_SPEED *
		i.golfer.speedMult *
		tier.power *
		i.contact.smash *
		golden *
		phone *
		lie.speedMult *
		club

	// Aim error is the golfer's, widened by anger. The tuning law from the plan:
	// the player's own offset must stay the largest term in the outcome, so this
	// stays comfortably inside one quality tier at low anger.
	const sigma =
		Math.hypot(i.golfer.dispersionDeg, tier.dispersionDeg) * 0.5 +
		(i.phone ? T.PHONE_DISPERSION : 0)
	const aimErr = i.rng.gaussian(sigma)
	const strikeSide = i.rng.next() < 0.5 ? -1 : 1
	// Aim at the flag, not down the middle: from an offline lie a golfer turns to
	// face the hole, so the errors below are relative to the line they picked.
	const bearing = i.target?.bearingDeg ?? 0
	const headingDeg = bearing + aimErr + strikeSide * (i.contact.offlineDeg + lie.offlineDeg)

	return {
		speed,
		launchDeg: Math.max(
			0.5,
			Math.min(launchDegFor(i.contact) + lie.launchDeg, lie.maxLaunchDeg ?? 90),
		),
		headingDeg,
		spinBack: i.contact.spinBack * (lie.spinMult ?? 1),
		// Pushed shots curve too, which is why a shank keeps going.
		spinSide: headingDeg * 0.06,
	}
}

/** Stamp copy. The ball never speaks, so the game's voice lives in these. */
export function stampFor(c: Contact): { main: string; sub: string } {
	switch (c.tier) {
		case 'FLUSH':
			return { main: 'PURE!', sub: 'dead centre' }
		case 'GOOD':
			return { main: 'solid.', sub: c.offset < 0 ? 'a touch low' : 'a touch high' }
		case 'THIN':
			return c.offset < 0
				? { main: 'TOPPED', sub: 'caught your crown' }
				: { main: 'SKIED', sub: 'went under you' }
		case 'SHANK':
			return { main: 'SHANK', sub: 'off the heel' }
		default:
			return { main: 'WHIFF', sub: 'you ducked' }
	}
}
